import { SiGoogle, SiApple } from "@icons-pack/react-simple-icons";
import { Button } from "../../ui/button";
import { useMemo } from "react";
import { Toggl } from "./Toggl";
import { cn } from "@/lib/utils";
import { GoogleIcon } from "@/components/Icons/GoogleIcon";
import { GithubIcon } from "@/components/Icons/GithubIcon";
import { GitlabIcon } from "@/components/Icons/GitlabIcon";
import { TrelloIcon } from "@/components/Icons/TrelloIcon";
import { GitHubPersonal } from "./GitHubPersonal";
import { IntegrationIcon } from "@/lib/icons";
import { startConnect } from "@/lib/auth/connect";
import { toast } from "sonner";
import { PlusIcon } from "lucide-react";
import { Bitrix24 } from "./Bitrix24.tsx";
import { Bitrix24Icon } from "@/components/Icons/Bitrix24Icon";
import { captureEvent } from "@/lib/analytics";
import { useElement } from "@/hooks/useElementProvider";

export const Connect = ({
  service,
  title,
  size,
  scopes,
}: { service: string; scopes?: string[] } & (
  | {
      title: string;
      size: "lg";
    }
  | {
      title: string;
      size: "default";
    }
  | {
      title?: string;
      size: "icon";
    }
  | {
      size: "sm";
      title?: string;
    }
)) => {
  const { elementDetails } = useElement();
  const requestedScopes =
    scopes ?? elementDetails.integrationScopes?.[service] ?? [];

  const icon = useMemo(() => {
    if (size === "sm") {
      return <PlusIcon />;
    }

    if (service === "google") {
      return size === "lg" ? <GoogleIcon /> : <SiGoogle size="16px" />;
    } else if (service === "apple") {
      return size === "lg" ? <SiApple /> : <SiApple size="16px" />;
    } else if (service === "github" || service === "github_personal") {
      return size === "lg" ? (
        <GithubIcon />
      ) : (
        <IntegrationIcon integration="github" size="16px" />
      );
    } else if (service === "gitlab") {
      return size === "lg" ? (
        <GitlabIcon />
      ) : (
        <IntegrationIcon integration="gitlab" size="16px" />
      );
    } else if (service === "trello") {
      return size === "lg" ? (
        <TrelloIcon />
      ) : (
        <IntegrationIcon integration="trello" size="16px" />
      );
    } else if (service === "toggl") {
      return <IntegrationIcon integration="toggl" size="16px" />;
    } else if (service === "atlassian") {
      return <IntegrationIcon integration="atlassian" size="16px" />;
    } else if (service === "pinterest") {
      return <IntegrationIcon integration="pinterest" size="16px" />;
    } else if (service === "linear") {
      return <IntegrationIcon integration="linear" size="16px" />;
    } else if (service === "bitrix24") {
      return size === "lg" ? (
        <Bitrix24Icon />
      ) : (
        <IntegrationIcon
          integration="bitrix24"
          defaultIcon={Bitrix24Icon}
          size="16px"
        />
      );
    } else if (service === "slack") {
      return <IntegrationIcon integration="slack" size="16px" />;
    } else {
      return null;
    }
  }, [service, size]);

  const colors = useMemo(() => {
    if (service === "trello") {
      return "bg-[#0C61DB] hover:bg-[#0C61DB]/50 text-[#FAFAFA] hover:text-[#FAFAFA]";
    } else if (service === "apple") {
      return "bg-[#FAFAFA] hover:bg-[#FAFAFA]/80 text-[#0A0A0A] hover:text-[#0A0A0A]";
    } else if (service === "github") {
      return "bg-[#24292F] hover:bg-[#24292F]/50 text-[#FAFAFA] hover:text-[#FAFAFA]";
    } else if (service === "bitrix24") {
      return "bg-[#1BA6F0] hover:bg-[#1BA6F0]/70 text-[#FAFAFA] hover:text-[#FAFAFA]";
    } else if (service === "slack") {
      return "bg-[#4A154B] hover:bg-[#4A154B]/80 text-[#FAFAFA] hover:text-[#FAFAFA]";
    } else {
      return "bg-[#FAFAFA] hover:bg-[#FAFAFA]/80 text-[#0A0A0A] hover:text-[#0A0A0A]";
    }
  }, [service]);

  const buttonClassName = useMemo(
    () =>
      cn({
        [`${colors} h-auto gap-4 rounded-xl py-4`]: size === "lg",
        "size-6 [&_svg]:size-4": size === "sm",
      }),
    [colors, size],
  );

  const tryConnect = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();

    captureEvent("onboarding_connect_started", {
      service,
      flow: "oauth",
    });

    try {
      await startConnect(service, { scopes: requestedScopes });
    } catch (err) {
      captureEvent("onboarding_connect_failed", {
        service,
        flow: "oauth",
        reason: err instanceof Error ? err.message : "connect_start_failed",
      });
      toast.error(`Could not start connecting ${service}. Please try again.`);
    }
  };

  return (
    <>
      {service === "toggl" ? (
        <Toggl variant={size === "default" ? "secondary" : "ghost"} size={size}>
          <div className="relative">{icon}</div>
          {size === "default" ? title : null}
        </Toggl>
      ) : service === "bitrix24" ? (
        <Bitrix24
          variant={size === "default" ? "secondary" : "ghost"}
          size={size}
          className={buttonClassName}
        >
          <div className="relative">{icon}</div>
          {size === "default" ? title : null}
        </Bitrix24>
      ) : service === "github_personal" ? (
        <GitHubPersonal
          variant={size === "default" ? "secondary" : "ghost"}
          size={size}
        >
          <div className="relative">{icon}</div>
          {size === "default" ? title : null}
        </GitHubPersonal>
      ) : (
        <Button
          variant={size === "default" || size === "sm" ? "secondary" : "ghost"}
          size={size === "sm" ? "icon" : size}
          className={buttonClassName}
          onClick={tryConnect}
          type="button"
        >
          <div className="relative">{icon}</div>
          {size !== "icon" ? title : null}
        </Button>
      )}
    </>
  );
};
